import type { ReactNode } from "react";
import { Redirect, useLocation } from "wouter";
import { privateBetaForPath } from "@shared/private-property-betas";
import { isHiddenAppPath } from "@shared/hidden-sections";
import { useAppFlags } from "@/hooks/use-app-flags";
import AppLayout from "./app-layout";
import { useBetaAccessState } from "@/hooks/use-beta-access";
import { Skeleton } from "@/components/ui/skeleton";

/** Do not mount protected children or their queries until access is granted. */
export function BetaFeatureRoute({ featureKey, children }: { featureKey: string; children: ReactNode }) {
  const { granted, loaded } = useBetaAccessState(featureKey);
  if (!loaded) return <AppLayout><Skeleton className="m-6 h-24" /></AppLayout>;
  if (!granted) return <AppLayout><div className="p-6" data-testid="beta-feature-not-found"><h1 className="text-xl font-semibold">Page not found</h1></div></AppLayout>;
  return <>{children}</>;
}

function isDailyPulsePath(location: string): boolean {
  const pathname = location.split(/[?#]/, 1)[0];
  return pathname === "/app/daily-pulse" || pathname.startsWith("/app/daily-pulse/");
}

/**
 * Protect entire route families, including present and future detail routes.
 *
 * B18/B19: sections switched off in shared/hidden-sections.ts, and Daily Pulse
 * while the `dailyPulseEnabled` app flag is off, redirect to the dashboard
 * before their page (or its queries) ever mounts.
 */
export function PrivatePropertyBetaRoutes({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  const dailyPulse = isDailyPulsePath(location);
  // Only fetch the flags when they matter, so public pages make no extra call.
  const { flags, loaded } = useAppFlags({ enabled: dailyPulse });
  if (isHiddenAppPath(location)) return <Redirect to="/app/dashboard" replace />;
  if (dailyPulse && !flags.dailyPulseEnabled) {
    return loaded ? <Redirect to="/app/dashboard" replace /> : null;
  }
  const feature = privateBetaForPath(location);
  return feature
    ? <BetaFeatureRoute featureKey={feature.key}>{children}</BetaFeatureRoute>
    : <>{children}</>;
}